import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import StudentLayout from "@/components/StudentLayout";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { KeyRound, IdCard, User, Phone, MapPin, Calendar, Loader2, GraduationCap, MonitorSmartphone, LogOut } from "lucide-react";
import PresenceDot from "@/components/PresenceDot";
import useIdleStatus from "@/lib/useIdleStatus";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

const initials = (name = "") =>
  (name.trim().split(/\s+/).map((p) => p[0]).join("").slice(0, 2) || "?").toUpperCase();

function bust() { return `v=${Date.now()}`; }

export default function StudentProfile() {
  const { student } = useAuth();
  const presenceStatus = useIdleStatus();
  const [photoVer, setPhotoVer] = useState(0);
  const [classTeacher, setClassTeacher] = useState(null);
  const [devices, setDevices] = useState([]);
  const [maxDevices, setMaxDevices] = useState(3);
  const [signingOut, setSigningOut] = useState(null);

  const loadDevices = () =>
    api.get("/student/devices").then((r) => { setDevices(r.data?.devices || []); setMaxDevices(r.data?.max_devices || 3); }).catch(() => {});

  useEffect(() => {
    api.get("/student/me").then((r) => setClassTeacher(r.data?.class_teacher || null)).catch(() => {});
    loadDevices();
  }, []);

  const signOutDevice = async (d) => {
    setSigningOut(d.device_id);
    try {
      const { data } = await api.delete(`/student/devices/${d.device_id}`);
      if (data.is_current) {
        toast.success("Signed out of this device");
        localStorage.removeItem("sa_student_token");
        window.location.href = "/login";
        return;
      }
      toast.success(`Signed out "${d.label}"`);
      loadDevices();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not sign out device");
    } finally {
      setSigningOut(null);
    }
  };

  // refresh photo cache-bust on hot-reload
  useEffect(() => { setPhotoVer((v) => v + 1); }, [student?.id]);

  if (!student) return null;

  const photoUrl = student.has_photo
    ? `${process.env.REACT_APP_BACKEND_URL}/api/profile/photo/${student.id}?${bust()}-${photoVer}`
    : null;

  return (
    <StudentLayout>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">My profile</div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>{student.name}</h1>
        <p className="text-slate-500 mt-1">Class {student.class_name} · Section {student.section}</p>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6" data-testid="student-profile-card">
        {/* Avatar block */}
        <div className="flex items-center gap-5 mb-6 pb-6 border-b border-slate-100 dark:border-slate-700" data-testid="student-avatar-block">
          <div className="relative">
            <div className="h-24 w-24 sm:h-28 sm:w-28 rounded-2xl overflow-hidden bg-gradient-to-br from-sky-100 to-violet-100 dark:from-sky-900/40 dark:to-violet-900/40 ring-4 ring-white dark:ring-slate-800 shadow-md flex items-center justify-center" data-testid="student-avatar">
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt={student.name}
                  className="h-full w-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <span className="text-3xl sm:text-4xl font-bold text-slate-500 dark:text-slate-300" style={{ fontFamily: "Outfit" }}>{initials(student.name)}</span>
              )}
            </div>
            {/* Presence dot — top-left */}
            <span
              className="absolute -top-1 -left-1 inline-flex p-1 rounded-full bg-white dark:bg-slate-900 shadow"
              title={presenceStatus === "online" ? "You are online" : "You are away (idle)"}
              data-testid="student-profile-presence-dot"
            >
              <PresenceDot status={presenceStatus} size="md" />
            </span>
          </div>
          <div className="min-w-0">
            <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">Profile photo</div>
            <div className="text-xs text-slate-500 mt-0.5">Your photo is managed by the school. Contact your class teacher to update it.</div>
          </div>
        </div>

        <dl className="grid sm:grid-cols-2 gap-4 text-sm">
          <Field icon={IdCard} label="Student ID" value={student.student_id || "—"}/>
          <Field icon={IdCard} label="Roll number" value={student.roll_number}/>
          <Field icon={GraduationCap} label="Class teacher" value={classTeacher?.name || "—"}/>
          <Field icon={User} label="Gender" value={student.gender || "—"}/>
          <Field icon={Calendar} label="Date of birth" value={student.dob || "—"}/>
          <Field icon={User} label="Parent name" value={student.parent_name || "—"}/>
          <Field icon={Phone} label="Parent phone" value={student.parent_phone || "—"}/>
          <Field icon={MapPin} label="Address" value={student.address || "—"}/>
        </dl>

        <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-700">
          <Link to="/student/set-password">
            <Button variant="outline" className="rounded-full" data-testid="profile-change-pw-btn">
              <KeyRound className="h-4 w-4 mr-1" /> Change password
            </Button>
          </Link>
        </div>
      </div>

      {/* Signed-in devices */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 mt-6" data-testid="student-devices-card">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <MonitorSmartphone className="h-5 w-5 text-sky-500" /> Signed-in devices
          </h2>
          <span className="text-xs text-slate-400" data-testid="devices-count">{devices.length}/{maxDevices}</span>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">You can be signed in on up to {maxDevices} devices. Sign out any you no longer use.</p>
        {devices.length === 0 ? (
          <div className="text-sm text-slate-400 py-4 text-center" data-testid="devices-empty">No other devices.</div>
        ) : (
          <ul className="space-y-2" data-testid="devices-list">
            {devices.map((d) => (
              <li key={d.device_id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700" data-testid={`device-row-${d.device_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-500 flex items-center justify-center shrink-0">
                    <MonitorSmartphone className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 dark:text-slate-100 truncate flex items-center gap-2">
                      {d.label}
                      {d.is_current && <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">This device</span>}
                    </div>
                    <div className="text-xs text-slate-400">Last used {d.last_login_at ? new Date(d.last_login_at).toLocaleString() : "—"}</div>
                  </div>
                </div>
                <Button
                  variant="ghost" size="sm"
                  className="rounded-full text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 shrink-0"
                  onClick={() => signOutDevice(d)}
                  disabled={signingOut === d.device_id}
                  data-testid={`device-signout-${d.device_id}`}
                >
                  {signingOut === d.device_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogOut className="h-4 w-4 mr-1" /> Sign out</>}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </StudentLayout>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-500 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-400">{label}</div>
        <div className="text-slate-900 dark:text-slate-100 font-medium truncate">{value}</div>
      </div>
    </div>
  );
}
